import { REFERRER_NONE, isApplicationInquiryKind } from "@kansai-ai-hub/schema";
import type { REFERRER_OPTIONS } from "@kansai-ai-hub/schema";

import type { InquiryKind, InquiryStatus } from "./queries";

/**
 * 「お問い合わせ管理」のタブ定義。
 *
 * 4 種別すべてに流入経路がある。LP 由来の 2 つは LP の Vercel Function からの転送、
 * ポータルの利用申込 2 つは公開フォーム（/apply/enterprise・/apply/member）から入る。
 *
 * `pending` は「タブは用意したが流入経路がまだ無い」種別のための仕組み。
 * 現在該当は無いが、将来 kind を先行追加するときのために残してある。
 */
export type InquiryTab = {
  kind: InquiryKind;
  label: string;
  /** まだ流入経路が無いタブ。空リストではなく「準備中」の説明を出す。 */
  pending?: boolean;
};

export const INQUIRY_TABS: InquiryTab[] = [
  { kind: "CONTACT", label: "LPお問い合わせ" },
  { kind: "RESERVATION", label: "LP検証環境問い合わせ" },
  { kind: "SEMINAR", label: "LPセミナー申込" },
  { kind: "ENTERPRISE_APPLICATION", label: "portal企業側申請" },
  { kind: "MEMBER_APPLICATION", label: "portal会員側申請" },
];

export const DEFAULT_INQUIRY_TAB: InquiryKind = "CONTACT";

export const INQUIRY_KIND_LABELS: Record<InquiryKind, string> = {
  CONTACT: "LPお問い合わせ",
  RESERVATION: "LP検証環境問い合わせ",
  SEMINAR: "LPセミナー申込",
  ENTERPRISE_APPLICATION: "portal企業側申請",
  MEMBER_APPLICATION: "portal会員側申請",
};

// 対応状況の表示名は packages/schema が唯一の出所（サーバーのエラーメッセージも同じ表を使う）。
// ここで別に持つと必ず言葉がずれる。
export { inquiryStatusLabel } from "@kansai-ai-hub/schema";

/**
 * その kind で実際に取りうる状態（絞り込みの選択肢に使う）。
 * 遷移表（`INQUIRY_STATUS_TRANSITIONS`）から機械的に導けなくはないが、
 * 「並び順」は業務の進み方に合わせたいので明示的に持つ。
 */
const LP_INQUIRY_STATUSES = ["NEW", "IN_PROGRESS", "DONE", "SPAM"] as const;
// 申請に「迷惑」は無い（遷移表でも進めない）。審査の結論は承認 / 非承認の 2 つ。
const APPLICATION_INQUIRY_STATUSES = [
  "NEW",
  "IN_PROGRESS",
  "APPROVED",
  "ACCOUNT_CREATED",
  "REJECTED",
] as const;

/**
 * 既定で畳んでおく状態。**手を動かす必要があるものだけ開いた状態で見せる**のが目的。
 * 終わったもの（回答済み・発行済み・非承認・迷惑）まで開いていると、
 * 新着が下へ押し出されて「今やること」が視界から消える。
 */
export const INQUIRY_STATUS_COLLAPSED_BY_DEFAULT: readonly InquiryStatus[] = [
  "DONE",
  "SPAM",
  "ACCOUNT_CREATED",
  "REJECTED",
  // `APPROVED` は**入れない**。申請フローではこれは終端ではなく
  // 「次にアカウントを発行する」作業待ちの状態（終端は ACCOUNT_CREATED）。
  // 畳むと発行待ちの件数が初期表示から消える。
];

export function inquiryStatusesForKind(kind: InquiryKind): readonly InquiryStatus[] {
  return isApplicationInquiryKind(kind) ? APPLICATION_INQUIRY_STATUSES : LP_INQUIRY_STATUSES;
}

/**
 * バッジの色。一覧と詳細で違う色にならないよう 1 箇所で持つ。
 * 主色（brand-primary）が赤なので、否定の状態（非承認）に赤を使うと「新規」と見分けられない。
 * 非承認は status-negative（黒に近い濃いグレー）にし、赤は対応待ちの「新規」だけに使う。
 */
export const INQUIRY_STATUS_COLORS: Record<InquiryStatus, string> = {
  NEW: "brand-primary",
  IN_PROGRESS: "blue",
  DONE: "gray",
  SPAM: "gray",
  APPROVED: "teal",
  REJECTED: "status-negative",
  ACCOUNT_CREATED: "green",
};

/**
 * 遷移の理由入力を必須にする状態。
 *
 * 非承認は申込者に説明が要るうえ、後から「なぜ落としたのか」を追えないと
 * 同じ組織が再申込したときに判断できない。理由は `InquiryEvent.comment` に残る。
 * それ以外の遷移は一覧から 1 クリックで変えられることの方が価値が高いので挟まない。
 */
// 理由が必須の遷移は packages/schema が正（サーバーでも同じ条件で弾く）。
// ここで別に定義すると、画面だけ緩い/厳しいという食い違いが生まれる。
export { INQUIRY_STATUS_REASON_REQUIRED } from "@kansai-ai-hub/schema";

/** 種別固有項目（payload）のキーを日本語ラベルにする。未知のキーはそのまま表示する。 */
export const INQUIRY_PAYLOAD_LABELS: Record<string, string> = {
  // LP フォーム由来
  inquiryType: "お問い合わせ種別",
  subject: "件名",
  phone: "電話番号",
  attend: "参加方法",
  seminarTitle: "セミナー",
  // LP が回ごとに振る識別子。同じ題名の回が複数あるときの区別に使うので、
  // seminarTitle と併記する（ラベルを置かないと「その他」に生キーのまま出る）。
  seminarId: "セミナーID",
  // ポータルの利用申込由来
  homepageUrl: "ホームページURL",
  department: "部署",
  contactRole: "役職",
  // 企業申請では「自社が属する業界」。会員申請は `INQUIRY_PAYLOAD_LABELS_BY_KIND` で
  // 「得意とする業界」に上書きする（申込フォームのラベルと同じ）。
  industries: "業界",
  technologies: "得意とする技術",
  referrer: "推薦者",
  referrerName: "推薦者のお名前",
  notificationEmails: "通知先メールアドレス",
  isNamePublic: "企業名の公開",
  organizationLogoPath: "ロゴ画像",
  applicantNote: "備考（申込者）",
  agreedToTerms: "規約・ポリシーへの同意",
  note: "備考",
};

/**
 * 種別ごとのラベルの上書き。同じキーでも申込フォーム上の文言が種別で違うものだけ持つ。
 * 事務局が見る詳細と申込者が見たフォームで言葉が揃っていないと、問い合わせ対応で
 * 「どの欄のことか」が噛み合わない。
 *
 * - `industries`: 企業は「業界」（自社が属する業界）、会員は「得意とする業界」
 *   （`application-form.tsx` の `industryLabel`）。
 */
export const INQUIRY_PAYLOAD_LABELS_BY_KIND: Partial<Record<InquiryKind, Record<string, string>>> =
  {
    MEMBER_APPLICATION: { industries: "得意とする業界" },
  };

/** payload のキーを、その種別のフォームで使われていたラベルにする。未知のキーはそのまま。 */
export function payloadFieldLabel(kind: InquiryKind, key: string): string {
  // ラベル表は継承プロパティ（toString 等）を拾わないよう hasOwn で引く。
  // `noUncheckedIndexedAccess` で添字の結果は string | undefined になるので `??` で受ける。
  const byKind = INQUIRY_PAYLOAD_LABELS_BY_KIND[kind];
  if (byKind && Object.hasOwn(byKind, key)) return byKind[key] ?? key;
  if (Object.hasOwn(INQUIRY_PAYLOAD_LABELS, key)) return INQUIRY_PAYLOAD_LABELS[key] ?? key;
  return key;
}

/**
 * 推薦者（団体）を詳細画面のヘッダにバッジで出すときの表記と色。
 *
 * キーは `REFERRER_OPTIONS` の値そのもの。添字（`REFERRER_OPTIONS[1]`）で書くと選択肢の
 * 並び替えで黙って別の団体を指すため、文言で書いて `satisfies` で型に守らせる
 * （inquiry-default-assignee.ts の LENOVO_REFERRER と同じ方針）。この形なら
 * **選択肢を足したのにここを足し忘れる**とコンパイルエラーになる。
 *
 * 正式名称はヘッダに並べるには長いので、バッジには短い呼称だけを出す
 * （正式名称は本文側の「推薦者」欄と、バッジの title で読める）。
 * 「なし」は推薦が無いという意味なので、ここには持たず、バッジ自体を出さない。
 */
export const REFERRER_BADGES = {
  "レノボ・ジャパン合同会社・レノボ・エンタープライズ・ソリューションズ合同会社": {
    label: "レノボ",
    color: "brand-red",
  },
  インテル株式会社: { label: "インテル", color: "blue" },
  // 主色（赤）はレノボ（brand-red）と同色なので、大阪取引所は専用トークン（旧ワイン色）で区別する。
  株式会社大阪取引所グループ: { label: "大阪取引所", color: "osaka-exchange" },
  "Nakanoshima Qross（中之島クロス）": { label: "中之島クロス", color: "teal" },
  "JAM BASE": { label: "JAM BASE", color: "green" },
  その他: { label: "その他", color: "gray" },
} as const satisfies Record<
  Exclude<(typeof REFERRER_OPTIONS)[number], typeof REFERRER_NONE>,
  { label: string; color: string }
>;

/**
 * payload の `referrer` からバッジの表記と色を決める。出さない場合は null を返す。
 *
 * - 推薦者が無い種別（LP 由来の問い合わせなど）・未入力・「なし」→ 出さない。
 * - 選択肢に無い値 → 選択肢の入れ替え前に届いた記録などが該当する。取りこぼさないよう、
 *   色は「その他」と同じ灰色にして、文言は届いた値をそのまま出す。
 */
export function resolveReferrerBadge(
  value: string | string[] | undefined,
): { label: string; color: string } | null {
  if (typeof value !== "string") return null;
  const referrer = value.trim();
  if (!referrer || referrer === REFERRER_NONE) return null;
  // ラベル表は継承プロパティ（toString 等）を拾わないよう hasOwn で引く。
  if (Object.hasOwn(REFERRER_BADGES, referrer)) {
    return REFERRER_BADGES[referrer as keyof typeof REFERRER_BADGES];
  }
  return { label: referrer, color: "gray" };
}

/** payload の値をそのまま出さず、日本語表示に置き換えるキーと対応表。 */
export const INQUIRY_PAYLOAD_VALUE_LABELS: Record<string, Record<string, string>> = {
  isNamePublic: { true: "公開する", false: "公開しない" },
};

/**
 * 「ご担当者」グループの見出し。詳細画面はこのグループの先頭に氏名（`Inquiry.name`）を
 * 差し込む。文言で照合すると見出しを変えたときに黙ってずれるため、定数で共有する。
 */
export const CONTACT_GROUP_TITLE = "ご担当者";

/**
 * 「組織について」グループの見出し。申請（ENTERPRISE / MEMBER_APPLICATION）では本文
 * （組織概要）をこのグループの先頭に差し込むので、`CONTACT_GROUP_TITLE` と同じく定数で共有する。
 */
export const ORGANIZATION_GROUP_TITLE = "組織について";

const CONTENT_GROUP_TITLE = "申込・お問い合わせの内容";

export type PayloadFieldGroup = { title: string; keys: string[] };

/**
 * 詳細画面での payload の並び。**種別ごとに「そのフォームにある項目」を固定で持つ。**
 *
 * payload は JSONB で、しかも保存側は空欄のキーを落とす（`inquiries.service.ts` の
 * 正規化）。そのまま `Object.entries` で回すと、並びが挿入順に依存するうえ、
 * 未入力の項目は行ごと消えて「フォームにその項目が無かった」のと区別できない。
 * 事務局が毎回同じ位置に同じ項目を見られるよう、ここで種別ごとの項目とグループを
 * 固定し、値の無い行は詳細画面が「未入力」と出す。ここに無いキーは「その他」に回して
 * 取りこぼさない。
 *
 * ポータル申請の項目は `application-form.tsx` と揃える（`organizationLogoPath` と
 * `isNamePublic` は企業のみ、`technologies` は会員のみ）。LP 由来の項目は LP 側（別リポジトリ）のフォームが送るキー。
 *
 * - CONTACT は LP の実フォームで確認済み: 担当者名 / 組織名 / メールアドレス /
 *   お問い合わせ種別 / 件名 / お問い合わせ内容（+ 同意）。**電話番号は無い**。
 *   前 3 つと本文は Inquiry 本体のカラム（name / organization / email / body）に入るので、
 *   payload に残るのは種別（inquiryType）と件名（subject）だけ。
 * - RESERVATION / SEMINAR は実フォームと突き合わせていないため、**固定リストとしては
 *   使わない**（`showsMissingPayloadFields` が false）。ここでの定義は並び順とグループ分けの
 *   指定だけで、payload に届いた項目しか出さず「未入力」も出さない。突き合わせが済んだら
 *   `KINDS_WITH_FIXED_FIELDS` に足すだけで固定リストに切り替わる。
 */
const LP_CONTACT_FIELDS: PayloadFieldGroup[] = [{ title: CONTACT_GROUP_TITLE, keys: ["phone"] }];

const APPLICATION_CONTACT_FIELDS: PayloadFieldGroup = {
  title: CONTACT_GROUP_TITLE,
  keys: ["department", "contactRole"],
};

/**
 * 組織についての項目。企業 / 会員で持つ項目が違うので、共通部分を関数で組む。
 * ロゴ画像と企業名の公開は企業のみ（企業一覧のカードに出すため）、得意とする技術は会員のみ。
 */
function applicationOrganizationFields(kind: "ENTERPRISE_APPLICATION" | "MEMBER_APPLICATION") {
  return {
    title: ORGANIZATION_GROUP_TITLE,
    keys: [
      "homepageUrl",
      ...(kind === "ENTERPRISE_APPLICATION" ? ["organizationLogoPath"] : []),
      "industries",
      ...(kind === "MEMBER_APPLICATION" ? ["technologies"] : []),
      ...(kind === "ENTERPRISE_APPLICATION" ? ["isNamePublic"] : []),
      "referrer",
      "referrerName",
      "notificationEmails",
    ],
  };
}

export const PAYLOAD_FIELD_GROUPS_BY_KIND: Record<InquiryKind, PayloadFieldGroup[]> = {
  CONTACT: [{ title: CONTENT_GROUP_TITLE, keys: ["inquiryType", "subject"] }],
  RESERVATION: [...LP_CONTACT_FIELDS, { title: CONTENT_GROUP_TITLE, keys: ["inquiryType"] }],
  SEMINAR: [
    ...LP_CONTACT_FIELDS,
    { title: CONTENT_GROUP_TITLE, keys: ["seminarTitle", "seminarId", "attend"] },
  ],
  ENTERPRISE_APPLICATION: [
    APPLICATION_CONTACT_FIELDS,
    applicationOrganizationFields("ENTERPRISE_APPLICATION"),
    { title: CONTENT_GROUP_TITLE, keys: ["applicantNote"] },
  ],
  MEMBER_APPLICATION: [
    APPLICATION_CONTACT_FIELDS,
    applicationOrganizationFields("MEMBER_APPLICATION"),
    { title: CONTENT_GROUP_TITLE, keys: ["applicantNote"] },
  ],
};

/**
 * 「フォームにある項目を固定で出し、値が無ければ『未入力』と出す」を適用する種別。
 * 実フォームと突き合わせて項目が確定しているものだけ。それ以外は payload に届いた
 * 項目だけを出す（届いていない項目は行ごと出さない）。
 */
const KINDS_WITH_FIXED_FIELDS: ReadonlySet<InquiryKind> = new Set<InquiryKind>([
  "CONTACT",
  "ENTERPRISE_APPLICATION",
  "MEMBER_APPLICATION",
]);

export function showsMissingPayloadFields(kind: InquiryKind): boolean {
  return KINDS_WITH_FIXED_FIELDS.has(kind);
}

/**
 * 固定リストにあっても、その申込では**入力する場面が無かった**項目は行ごと出さない。
 * 「未入力」と出すと、申込者が空欄にしたように読めてしまう。
 *
 * - `referrerName`: 推薦者が「なし」または未選択のとき、フォームは人名を保存しない
 *   （`inquiries.service.ts`）。推薦が無いのに「推薦者のお名前: 未入力」は誤解を招く。
 */
export function isPayloadFieldApplicable(
  key: string,
  payload: Record<string, string | string[]>,
): boolean {
  if (key === "referrerName") {
    const referrer = payload.referrer;
    return typeof referrer === "string" && referrer.trim() !== "" && referrer !== REFERRER_NONE;
  }
  return true;
}

/**
 * 「未入力」と扱う値。保存側は空欄のキーを落とすので通常は `undefined` だが、
 * LP の取り込み口は任意の payload を受けるため、空文字や空配列も同じ扱いにする。
 */
export function isEmptyPayloadValue(value: string | string[] | undefined): boolean {
  if (value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return value.trim() === "";
}

/**
 * 一覧テーブルの「種別で中身が変わる列」（内容の抜粋と送信日時の間）。
 *
 * 種別によって一覧から拾いたい 1 項目が違う。申請では「誰の紹介で来たか」、LP 由来では
 * 「何の問い合わせか」「どの回のセミナーか」が、詳細を開くかどうかの判断材料になる。
 * どれも payload の 1 キーなので、列として 1 本持たせ、中身だけ差し替える。
 *
 * `field` は `AdminInquiryListItem` のキー。`width` は `layout="fixed"` の列幅そのもので、
 * 自由入力（セミナー名・問い合わせ種別）は推薦バッジより長くなるため広く取る。
 */
export const INQUIRY_EXTRA_COLUMNS: Record<
  InquiryKind,
  { header: string; field: "referrer" | "inquiryType" | "seminarTitle"; width: number }
> = {
  CONTACT: { header: "お問い合わせ種別", field: "inquiryType", width: 180 },
  RESERVATION: { header: "お問い合わせ種別", field: "inquiryType", width: 180 },
  SEMINAR: { header: "セミナー", field: "seminarTitle", width: 220 },
  ENTERPRISE_APPLICATION: { header: "推薦", field: "referrer", width: 116 },
  MEMBER_APPLICATION: { header: "推薦", field: "referrer", width: 116 },
};

/** 本文の見出しは種別で変える（同じ「内容」でも意味が違うため）。 */
export const BODY_HEADINGS: Record<InquiryKind, string> = {
  CONTACT: "お問い合わせ内容",
  // LP のフォームが「ご予約」から「お問い合わせ」に変わり、自由記述欄の名称も
  // 「想定している検証環境のご利用内容」から「お問い合わせ内容」になった。
  // 変更前に届いた記録もこの見出しで表示されるが、本文そのものは変わらない。
  RESERVATION: "お問い合わせ内容",
  SEMINAR: "備考",
  ENTERPRISE_APPLICATION: "組織概要",
  MEMBER_APPLICATION: "組織概要",
};

/** 一覧の「内容」列の見出しも種別に合わせる。 */
export const EXCERPT_HEADINGS: Record<InquiryKind, string> = BODY_HEADINGS;

/**
 * この申込からアカウントを発行できるか（押す前に分かる事前判定）。
 *
 * サーバー（inquiry-account.service.ts）が 409 で弾く条件のうち、画面の持つ情報で判定できる
 * もの: 利用申込であること・ログインパスワード設定済み・組織名あり。
 * 一覧の行・詳細の対応状況メニュー・詳細の「アカウントを作成」ボタンがすべてこれを使う。
 * 片方にだけ条件を書くと、押せるのに 409 が返るボタンができる。
 */
export function canIssueAccount(inquiry: {
  kind: InquiryKind;
  hasPassword: boolean;
  organization: string | null;
}): boolean {
  return isApplicationInquiryKind(inquiry.kind) && inquiry.hasPassword && !!inquiry.organization;
}
